import { Button } from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, LoaderCircle, X } from 'lucide-react';
import * as React from 'react';

import type { DataTableState } from '@/design';
import type { IListField, IListOrder } from '../../api/lists-contracts';
import { LoadError } from '../../components/admin-page';
import { DEFAULT_LIST_PAGE_SIZE, TEAMS_SURVEY, catalogueSections, formatListValue } from '../../lists';
import { CriteriaBuilder } from '../lists/advanced-filter';
import { TeamPathMenu, sayProblem } from '../lists/area';
import { ColumnsEditor } from '../lists/columns-sheet';
import { elapsedWords } from '../lists/export-downloads';
import { FieldPicker, FieldSourcesHelp } from '../lists/field-picker';
import { ListGrid } from '../lists/grid';
import { GroupStrip } from '../lists/group-by';
import { useListRows } from '../lists/use-list-rows';
import { useReport } from './report';

/**
 * A report's Definition and Preview (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §4.2) — Salesforce's report builder,
 * over Lists' own components (principle 7): the columns sheet's editor split in two, **Fields** on
 * the left (search, the catalogue by group, a box that adds a column) and **Columns** in the main
 * column (ordered, ↑ / ↓, ✕); the *Advanced…* builder drawn inline as **Filters** — here the
 * conditions are the subject; **Sort**; **Group by** (a summary report), Lists' group strip with no
 * header to drag. Nothing here judges: survey-api's 400 lands on the builder's rows by path, and a
 * save's refusals are said by *Save ▾*.
 *
 * **Preview is the first page as survey-api answers it** — Lists' grid with sorting, Σ totals and
 * group headings, and **without funnels, editing, selection or the pane**: a report is a question
 * asked of the data. It reads once per definition, never polls (`polled: false`), and draws a
 * result only under the definition it answers.
 */

/** The Definition tab: Fields beside Filters, Columns, Sort and Group by. */
export function ReportDefinitionTab() {
  const report = useReport();
  return (
    <div className="grid gap-4">
      <div className="grid items-start gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <ColumnsEditor
          catalogue={report.catalogue}
          columns={report.columns}
          onChange={report.setColumns}
          split={({ fields, shown }) => (
            <>
              <aside
                aria-labelledby="report-fields"
                className="grid min-w-0 max-h-[70dvh] grid-cols-[minmax(0,1fr)] gap-2 overflow-y-auto rounded-md border border-border p-3 lg:sticky lg:top-4"
              >
                <div className="flex items-center gap-1">
                  <h2 id="report-fields" className="text-sm font-medium text-[var(--color-royal-blue)]">
                    Fields
                  </h2>
                  <FieldSourcesHelp />
                </div>
                {fields}
              </aside>
              <div className="grid min-w-0 gap-6">
                {report.survey === TEAMS_SURVEY && (
                  <div className="flex">
                    <TeamPathMenu
                      path={report.scope?.path ?? null}
                      onChoose={(path) => report.setScope({ ...(report.scope ?? {}), path })}
                    />
                  </div>
                )}
                <BuilderSection title="Filters" hook="filters">
                  <CriteriaBuilder
                    root={report.root}
                    catalogue={report.catalogue}
                    words={report.words}
                    placed={report.placed}
                    onChange={report.setRoot}
                  />
                </BuilderSection>
                <BuilderSection title="Columns" hook="columns">
                  {shown}
                </BuilderSection>
                <BuilderSection title="Sort" hook="sort">
                  <SortLevels />
                </BuilderSection>
                <BuilderSection title="Group by" hook="group-by">
                  <GroupStrip
                    groupBy={report.groupBy}
                    columns={report.columns}
                    fieldOf={report.fieldOf}
                    aggregates={null}
                    onChange={report.setGroupBy}
                    hint="Not grouped. A grouping makes a summary report — its headings are counted in Preview."
                  />
                </BuilderSection>
              </div>
            </>
          )}
        />
      </div>
    </div>
  );
}

function BuilderSection({ title, hook, children }: { title: string; hook: string; children: React.ReactNode }) {
  const id = React.useId();
  return (
    <section aria-labelledby={id} data-builder={hook} className="grid gap-2">
      <h2 id={id} className="text-sm font-medium text-[var(--color-royal-blue)]">
        {title}
      </h2>
      {children}
    </section>
  );
}

/**
 * The sort levels, outermost first: each its field, its order (a press reverses it), ↑ / ↓ and ✕;
 * *Add a sort* offers every sortable field of the catalogue not already sorted by, in the field
 * picker's folded groups and search (SR-403). survey-api sorts, blanks last.
 */
function SortLevels() {
  const report = useReport();
  const { sorts, setSorts, fieldOf } = report;
  const used = new Set(sorts.map((level) => level.field));
  const offered = catalogueSections(report.catalogue, (field) => field.sortable && !used.has(field.code));
  const change = (index: number, level: Nullable<IListOrder>) =>
    setSorts(
      level === null ? sorts.filter((_, at) => at !== index) : sorts.map((one, at) => (at === index ? level : one))
    );
  const move = (index: number, by: -1 | 1) => {
    const next = [...sorts];
    const [level] = next.splice(index, 1);
    next.splice(index + by, 0, level);
    setSorts(next);
  };

  return (
    <div className="grid gap-1.5 text-[0.8125rem]">
      {sorts.length === 0 && <p className="text-muted-foreground">Not sorted.</p>}
      <ol className="grid gap-1">
        {sorts.map((level, index) => {
          const label = fieldOf(level.field)?.label ?? level.field;
          const descending = level.dir === 'desc';
          return (
            <li key={level.field} data-sort={level.field} className="flex h-8 items-center gap-2">
              <span className="w-16 text-muted-foreground">{index === 0 ? 'Sort by' : 'then by'}</span>
              <span className="min-w-0 truncate font-medium">{label}</span>
              <Button
                type="button"
                variant="outline"
                className="h-7 gap-1 px-2 font-normal"
                aria-label={`${label}, ${descending ? 'descending' : 'ascending'} — reverse`}
                onClick={() => change(index, { field: level.field, dir: descending ? 'asc' : 'desc' })}
              >
                {descending ? <ArrowDown className="size-3.5" aria-hidden /> : <ArrowUp className="size-3.5" aria-hidden />}
                {descending ? 'Descending' : 'Ascending'}
              </Button>
              <span className="ml-auto flex gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  className="size-7 p-0"
                  aria-label={`Move the sort by ${label} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp className="size-3.5" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="size-7 p-0"
                  aria-label={`Move the sort by ${label} down`}
                  disabled={index === sorts.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown className="size-3.5" aria-hidden />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="size-7 p-0"
                  aria-label={`Remove the sort by ${label}`}
                  onClick={() => change(index, null)}
                >
                  <X className="size-3.5" aria-hidden />
                </Button>
              </span>
            </li>
          );
        })}
      </ol>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground" aria-hidden>
          {sorts.length === 0 ? 'Sort by' : 'Then by'}
        </span>
        <FieldPicker
          label={sorts.length === 0 ? 'Sort by' : 'Then by'}
          sections={offered}
          value=""
          placeholder="Add a sort…"
          disabled={offered.length === 0}
          onChoose={(field) => setSorts([...sorts, { field, dir: 'asc' }])}
          className="w-72 max-w-full"
        />
      </div>
    </div>
  );
}

/** Seconds since `active` turned on, ticking each second; 0 while off. */
function useElapsed(active: boolean): number {
  const [seconds, setSeconds] = React.useState(0);
  React.useEffect(() => {
    setSeconds(0);
    if (!active) return;
    const started = Date.now();
    const timer = window.setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [active]);
  return seconds;
}

/** No row is struck in a preview: it is read once, and nothing polls it. */
const NONE_STRUCK: ReadonlySet<string> = new Set();

/** The Preview tab: the first page, its totals and its group headings. */
export function ReportPreviewTab() {
  const report = useReport();
  const { columns, groupBy, rules, fieldOf, query } = report;
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(new Set());
  const known = (code: string) => fieldOf(code) !== undefined;

  // The read asks for what its cells need beside the columns: a grouping's fields and a rule's.
  const extra = [...groupBy.map((level) => level.field), ...rules.flatMap((rule) => (rule.field ? [rule.field] : []))]
    .filter((code, index, all) => known(code) && !columns.includes(code) && all.indexOf(code) === index);
  const readQuery = { ...query, columns: [...columns, ...extra] };
  const shown = columns.flatMap((code) => fieldOf(code) ?? []);
  const hidden = columns.filter((code) => !known(code)).length;
  const sums = shown.filter((field) => field.summable);
  const size = report.pageSize ?? DEFAULT_LIST_PAGE_SIZE;
  const list = useListRows({
    query: readQuery,
    page: 1,
    pageSize: size,
    sums: sums.map((field) => field.code),
    polled: false
  });
  const { read, error } = list;
  // A wide report draws the grid's own sentence far to the right, out of view: the status line
  // says the read is out, with its time, and the grid shows its loading rows (SR-418).
  const elapsed = useElapsed(list.reading);

  const state: DataTableState | undefined =
    read === null
      ? error
        ? {
            kind: 'error',
            content: (
              <LoadError
                message={
                  error.problems.length > 0
                    ? `This report could not be read: ${error.problems
                        .map((problem) => sayProblem(problem, readQuery, fieldOf))
                        .join('; ')}.`
                    : `This report could not be read. ${error.message}`
                }
              />
            )
          }
        : { kind: 'loading' }
      : read.rows.length === 0
        ? { kind: 'empty', content: 'No row matches this report.' }
        : undefined;

  return (
    <div className="grid min-w-0 gap-3">
      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <span role="status" className="inline-flex items-center gap-2">
          {list.reading ? (
            <>
              <LoaderCircle
                aria-hidden
                className="size-4 animate-spin text-[var(--color-sapphire)] motion-reduce:animate-none"
              />
              Reading the report…
              {/* The time ticks each second: out of the live region, or it is read aloud each tick. */}
              <span aria-hidden>{elapsedWords(elapsed * 1000)}</span>
            </>
          ) : read
            ? read.total > read.rows.length
              ? `The first ${read.rows.length.toLocaleString('en-US')} of ${read.total.toLocaleString('en-US')} rows — Run now or Export now writes every row.`
              : `${read.total.toLocaleString('en-US')} ${read.total === 1 ? 'row' : 'rows'}.`
            : ''}
        </span>
        <Button type="button" variant="outline" size="sm" disabled={list.reading} onClick={() => void list.refresh()}>
          Read again
        </Button>
      </div>
      {error && read !== null && (
        <LoadError message={`Could not refresh — showing the preview as last read. ${error.message}`} />
      )}
      {columns.length === 0 && (
        <p className="text-sm text-muted-foreground">This report shows no columns — add them on Definition.</p>
      )}
      {hidden > 0 && (
        <p className="text-sm text-muted-foreground">
          This report names {hidden === 1 ? 'a column' : `${hidden} columns`} you cannot see;{' '}
          {hidden === 1 ? 'it is' : 'they are'} left out.
        </p>
      )}
      <ListGrid
        label="Report preview"
        columns={shown}
        fieldOf={fieldOf}
        sourceOf={report.sourceOf}
        rows={read?.rows ?? null}
        state={state}
        struck={NONE_STRUCK}
        sorts={report.sorts}
        onSort={report.setSorts}
        root={report.root}
        query={readQuery}
        kindOf={report.kindOf}
        words={report.words}
        groupBy={groupBy}
        collapsed={collapsed}
        onToggleGroup={(key) =>
          setCollapsed((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          })
        }
        aggregates={read?.aggregates ?? null}
        sums={sums}
        rules={rules}
      />
      {sums.length > 0 && (
        <div data-grid-footer="" className="flex flex-wrap gap-x-4 gap-y-2 text-[0.8125rem] text-muted-foreground">
          {read?.aggregatesError ? (
            <span>The totals could not be read: {read.aggregatesError}</span>
          ) : (
            sums.map((field: IListField) => {
              const sum = read?.aggregates?.sums[field.code];
              return (
                <span key={field.code} data-total={field.code} className="tabular-nums">
                  Σ {field.label}{' '}
                  <span className="font-medium text-foreground">
                    {read?.aggregates ? (sum === null || sum === undefined ? '—' : formatListValue(field, sum)) : ''}
                  </span>
                </span>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
